'use client';

import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useMemo } from 'react';
import { usePathname } from 'next/navigation';
import type { AppRouterInstance } from 'next/dist/shared/lib/app-router-context.shared-runtime';
import { refreshLenis } from '@/components/LenisProvider';

type PreloaderState = 'hidden' | 'sliding-up' | 'covering' | 'sliding-down' | 'loading';

interface PreloaderContextType {
  state: PreloaderState;
  isInitialLoad: boolean;
  showLogo: boolean;
  isLoading: boolean;
  triggerTransition: (targetUrl: string, router: AppRouterInstance) => Promise<void>;
  completeLoading: () => void;
}

const PreloaderContext = createContext<PreloaderContextType | undefined>(undefined);

export const usePreloader = () => {
  const context = useContext(PreloaderContext);
  if (!context) {
    throw new Error('usePreloader must be used within a PreloaderProvider');
  }
  return context;
};

interface PreloaderProviderProps {
  children: ReactNode;
}

export const PreloaderProvider: React.FC<PreloaderProviderProps> = ({ children }) => {
  const [state, setState] = useState<PreloaderState>('covering'); // Start covering on initial load
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [showLogo, setShowLogo] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [targetPath, setTargetPath] = useState<string | null>(null);
  const pathname = usePathname();
  
  // Check if current path is an admin route
  const isAdminRoute = pathname.startsWith('/admin') || pathname.startsWith('/login');

  // Handle initial load
  useEffect(() => {
    if (isInitialLoad) {
      // Skip preloader for admin routes
      if (isAdminRoute) {
        setState('hidden');
        setIsInitialLoad(false);
        return;
      }
      
      // Initial load sequence:
      // 1.5s: Logo fades in
      const logoFadeInTimer = setTimeout(() => {
        setShowLogo(true);
      }, 1500);
      
      // 3.5s: Logo fades out (just before slide down starts)
      const logoFadeOutTimer = setTimeout(() => {
        setShowLogo(false);
      }, 3500);
      
      // 4s: Container slides down
      const slideDownTimer = setTimeout(() => {
        setState('sliding-down');
        setTimeout(() => {
          setState('hidden');
          setIsInitialLoad(false);
        }, 800); // Duration of slide down animation
      }, 4000);

      return () => {
        clearTimeout(logoFadeInTimer);
        clearTimeout(logoFadeOutTimer);
        clearTimeout(slideDownTimer);
      };
    }
  }, [isInitialLoad, isAdminRoute]);

  // Clear loading state when pathname changes to target (successful navigation)
  useEffect(() => {
    if (isLoading && targetPath && pathname === targetPath && !isInitialLoad) {
      // We've reached the target path, clear loading state
      setIsLoading(false);
      setTargetPath(null);
    }
  }, [pathname, isLoading, targetPath, isInitialLoad]);

  // Function to preload page content using Next.js prefetch
  const preloadPageContent = useCallback(async (url: string, router: AppRouterInstance): Promise<void> => {
    try {
      // Use Next.js router.prefetch to preload the page
      await router.prefetch(url);
      
      // Add a small delay to ensure prefetch is complete
      await new Promise(resolve => setTimeout(resolve, 200));
      
      return;
    } catch {
      // Continue anyway - don't block navigation on preload failures
      return;
    }
  }, []);

  // Function to trigger page transition with actual preloading
  const triggerTransition = useCallback(async (targetUrl: string, router: AppRouterInstance): Promise<void> => {
    if (isInitialLoad || isAdminRoute) return; // Don't trigger during initial load or on admin routes
    
    setIsLoading(true);
    setTargetPath(targetUrl);
    setState('sliding-up');
    
    // Start preloading immediately
    const preloadPromise = preloadPageContent(targetUrl, router);
    
    // Wait for slide up animation to complete (0.6s)
    await new Promise(resolve => setTimeout(resolve, 600));
    
    // Set covering state and show logo
    setState('covering');
    setShowLogo(true);
    
    // Wait for preloading to complete (minimum 0.4s for logo visibility)
    const minDisplayTime = new Promise(resolve => setTimeout(resolve, 400));
    await Promise.all([preloadPromise, minDisplayTime]);
    
    // Navigate to the new page with scroll disabled to prevent Next.js auto-scroll conflicts
    router.push(targetUrl, { scroll: false });
    
    // Refresh Lenis after navigation to ensure proper scroll calculation
    setTimeout(() => {
      refreshLenis();
    }, 100);
    
    // Keep loading state until pathname changes to targetUrl
    // This will be cleared by the useEffect above when pathname === targetPath
    return;
  }, [preloadPageContent, isInitialLoad, isAdminRoute]);

  // Function to complete the loading sequence and hide preloader
  const completeLoading = useCallback(() => {
    if (state !== 'covering' || isLoading) return;
    
    // Logo fade out
    setShowLogo(false);
    
    // Scroll to top before revealing the new page
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'instant'
    });
    
    // Start slide down after short delay
    setTimeout(() => {
      setState('sliding-down');
      setTimeout(() => {
        setState('hidden');
      }, 800); // Duration of slide down animation
    }, 200);
  }, [state, isLoading]);

  const value: PreloaderContextType = useMemo(() => ({
    state,
    isInitialLoad,
    showLogo,
    isLoading,
    triggerTransition,
    completeLoading,
  }), [state, isInitialLoad, showLogo, isLoading, triggerTransition, completeLoading]);

  return (
    <PreloaderContext.Provider value={value}>
      {children}
    </PreloaderContext.Provider>
  );
};